import { useCallback } from 'react';
import { useDividerDrag, useKeyboardStep } from './hooks';
import style from './draggable-divider.module.scss';

interface DraggableDividerProps {
  onDrag: (percent: number) => void;
  /**
   * The current split, as a percent. Supply it to enable keyboard resizing — the
   * handle has to know where it is to step from there. Omit it and the divider
   * stays drag-only, exactly as before.
   */
  value?: number;
  /** Accessible name for the handle, when it is keyboard-operable. */
  label?: string;
}

/** Percent moved per arrow press — the same clamp the drag uses. */
const KEYBOARD_STEP_PERCENT = 3;
const MIN_PERCENT = 15;
const MAX_PERCENT = 85;

/**
 * A vertical, percent-based splitter handle for a column of stacked regions.
 * Dragging reports the pointer's vertical position as a percentage of the parent
 * element's height (clamped to 15–85%), so the consumer can apply it as the two
 * regions' `flex-basis`. Pointer-driven; sets a `row-resize` cursor and suppresses
 * text selection while dragging.
 *
 * Shared component — extracted from `search-page-layout` so both the eval3-new
 * left pane and the search page layout use one implementation.
 *
 * @param {DraggableDividerProps} props - Component props.
 * @param {Function} props.onDrag - Called continuously during the drag with the new
 *   split position (15–85, percent of the parent's height).
 * @param {number} [props.value] - The current split percent. Supplying it turns the
 *   handle into a focusable separator that arrow keys can move, since dragging
 *   cannot be the only way to size a region.
 * @param {string} [props.label] - Accessible name, used when keyboard-operable.
 * @return {JSX.Element} The divider handle.
 */
function DraggableDivider({ onDrag, value, label }: DraggableDividerProps) {
  const onMove = useCallback(
    ({ clientY }: { clientY: number }, parent: DOMRect) => {
      const percent = ((clientY - parent.top) / parent.height) * 100;
      onDrag(Math.max(MIN_PERCENT, Math.min(MAX_PERCENT, percent)));
    },
    [onDrag]
  );
  const { ref, handlers } = useDividerDrag('row-resize', onMove);
  const onKeyDown = useKeyboardStep({
    value,
    min: MIN_PERCENT,
    max: MAX_PERCENT,
    step: KEYBOARD_STEP_PERCENT,
    decrease: 'ArrowUp',
    increase: 'ArrowDown',
    onChange: onDrag,
  });
  const keyboardEnabled = typeof value === 'number';

  return (
    <div
      ref={ref}
      className={style.divider}
      onKeyDown={onKeyDown}
      role={keyboardEnabled ? 'separator' : undefined}
      aria-orientation={keyboardEnabled ? 'horizontal' : undefined}
      aria-label={keyboardEnabled ? label : undefined}
      aria-valuenow={keyboardEnabled ? Math.round(value) : undefined}
      aria-valuemin={keyboardEnabled ? MIN_PERCENT : undefined}
      aria-valuemax={keyboardEnabled ? MAX_PERCENT : undefined}
      tabIndex={keyboardEnabled ? 0 : undefined}
      {...handlers}
    >
      <div className={style.handle} />
    </div>
  );
}

export { DraggableDivider };
